<template>
  <button
    v-if="isVisible"
    class="tour-help-button"
    type="button"
    :aria-label="buttonLabel"
    :title="buttonLabel"
    @click="openHelp"
  >
    <span class="tour-help-icon" aria-hidden="true"></span>
  </button>
</template>

<script>
import { computed } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { tour } from "./tour/tour.js";
import { steps as zapisySteps } from "./tour/sections/ciselneZapisy";
import { steps as videoSteps } from "./tour/sections/naucneVidea";
import { steps as classroomSteps } from "./tour/sections/mojaUcebna";

export default {
  name: "TourHelpButton",
  setup() {
    const route = useRoute();
    const { t } = useI18n();

    const configs = [
      {
        key: "zapisy",
        match: (path) =>
          path.startsWith("/ciselne-zapisy") ||
          path.startsWith("/ucebna/ciselny-zapis") ||
          path.startsWith("/ucebna/moje-piesne"),
        steps: zapisySteps,
        points: 150,
        label: () => t("tour.help.sections.scores"),
      },
      {
        key: "videos",
        match: (path) =>
          path.startsWith("/naucne-videa") ||
          path.startsWith("/ucebna/naucne-videa") ||
          path.startsWith("/ucebna/zadarmo-video") ||
          path.startsWith("/ucebna/moje-kurzy"),
        steps: videoSteps,
        points: 100,
        label: () => t("tour.help.sections.videos"),
      },
      {
        key: "classroom",
        match: (path) => path.startsWith("/ucebna"),
        steps: classroomSteps,
        points: 100,
        label: () => t("tour.help.sections.classroom"),
      },
    ];

    const activeConfig = computed(() => {
      const path = String(route.path || "");
      return configs.find((cfg) => cfg.match(path)) || null;
    });

    const isVisible = computed(
      () => !tour.state.open && Boolean(activeConfig.value)
    );

    const buttonLabel = computed(() => t("tour.help.buttonLabel"));

    const openHelp = () => {
      const cfg = activeConfig.value;
      if (!cfg) return;

      const sectionLabel = cfg.label();
      const steps = typeof cfg.steps === "function" ? cfg.steps() : [];

      tour.openBetween({
        title: t("tour.help.title"),
        text: t("tour.help.text", { section: sectionLabel }),
        options: [
          {
            label: t("tour.help.options.current"),
            action: "start-section",
            steps: cfg.steps,
          },
          {
            label: t("tour.help.options.full"),
            action: "start-full",
          },
        ],
        steps,
        program: {
          hidePoints: true,
          stages: [
            {
              key: cfg.key,
              name: cfg.key,
              label: sectionLabel,
              steps,
            },
          ],
        },
      });
    };

    return {
      isVisible,
      buttonLabel,
      openHelp,
    };
  },
};
</script>

<style scoped>
.tour-help-button {
  position: fixed;
  right: 0.6em;
  bottom: 0.6em;
  width: 1.9rem;
  height: 1.9rem;
  border-radius: 999px;
  border: none;
  background: transparent;
  color: #9abe4a;
  font-family: Bahnschrift;
  font-weight: 600;
  font-size: 1.1em;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  cursor: pointer;
  transition: transform 0.18s ease, text-shadow 0.18s ease, color 0.18s ease;
}

.tour-help-icon {
  width: 1.2rem;
  height: 1.2rem;
  display: block;
  background-color: currentColor;
  -webkit-mask: url("~@/assets/images/icons/info.svg") center/contain no-repeat;
  mask: url("~@/assets/images/icons/info.svg") center/contain no-repeat;
}

.tour-help-button:hover {
  transform: translateY(-2px) scale(1.02);
  text-shadow: 0 3px 12px rgba(154, 190, 74, 0.35);
  color: #a7ce55;
}

.tour-help-button:focus-visible {
  outline: none;
  transform: translateY(-2px) scale(1.02);
  text-shadow: 0 3px 12px rgba(154, 190, 74, 0.35);
  color: #a7ce55;
}

@media (prefers-reduced-motion: reduce) {
  .tour-help-button {
    transition: none;
  }
}
</style>
